import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { useUser } from '../context/UserContext';
import { useOAuth } from '../context/OAuthContext';
import ProductsManagement from '../components/admin/ProductsManagement';
import CategoriesManagement from '../components/admin/CategoriesManagement';
import TexturesManagement from '../components/admin/TexturesManagement';
import ImagesManagement from '../components/admin/ImagesManagement';
import GalleriesManagement from '../components/admin/GalleriesManagement';
import UsersManagement from '../components/admin/UsersManagement';
import RolesManagement from '../components/admin/RolesManagement';
import OrdersManagement from '../components/admin/OrdersManagement';
import SalesDataManagement from '../components/admin/SalesDataManagement';
import JobsManagement from '../components/admin/JobsManagement';
import ApplicationsManagement from '../components/admin/ApplicationsManagement';
import WebhooksManagement from '../components/admin/WebhooksManagement';
import DiscountCodesManagement from '../components/admin/DiscountCodesManagement';
import PartnersManagement from '../components/admin/PartnersManagement';
import NewsManagement from '../components/admin/NewsManagement';
import SiteSettingsManagement from '../components/admin/SiteSettingsManagement';
import MaintenanceManagement from '../components/admin/MaintenanceManagement';
import TeamManagement from '../components/admin/TeamManagement';
import PublicationsManagement from '../components/admin/PublicationsManagement';
import StaffRosterManagement from '../components/admin/StaffRosterManagement';
import LoginForm from '../components/admin/LoginForm';
import '../App.css';
import './Admin.css';

const PUBLICATION_PERMISSIONS = [
  'publications.create',
  'publications.edit',
  'publications.delete',
  'publications.control'
];

const STAFF_ROSTER_PERMISSIONS = [
  'staff_roster.view',
  'staff_roster.create',
  'staff_roster.edit',
  'staff_roster.archive',
  'staff_roster.delete',
  'staff_roster.assign_compensation',
  'staff_roster.manage_structure',
  'staff_roster.review_pending',
  'staff_roster.manage_approvals',
  'staff_roster.view_summaries',
  'staff_roster.view_history'
];

const Admin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('products');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const { isInitialized } = useAdmin();
  const { currentUser, isInitialized: userInitialized, hasPermission, hasAnyPermission, logout: userLogout, loading: userLoading, roles } = useUser();
  const { currentUser: oauthUser, isInitialized: oauthInitialized, logout: oauthLogout } = useOAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    oauthLogout();
    userLogout();
    navigate('/');
  };

  const isAdministrator = currentUser && roles.find(r => r.id === currentUser.roleId)?.name === 'Administrator';

  // Pages tab accessible to anyone with admin.settings
  const canAccessPages = hasPermission('admin.settings') || isAdministrator;
  const canManagePublications = hasAnyPermission(PUBLICATION_PERMISSIONS);
  const canManageStaffRoster = hasAnyPermission(STAFF_ROSTER_PERMISSIONS);

  const changeTab = (tab) => {
    setActiveTab(tab);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    if (searchParams.get('orderId')) {
      newSearchParams.set('orderId', searchParams.get('orderId'));
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (userInitialized && !currentUser) {
    }
  }, [currentUser, userInitialized]);

  useEffect(() => {
    if (oauthInitialized && oauthUser && oauthUser.adminUser && !currentUser) {
      const adminUserData = {
        id: oauthUser.adminUser.id,
        username: oauthUser.adminUser.username,
        email: oauthUser.adminUser.email,
        roleId: oauthUser.adminUser.roleId,
        role: oauthUser.adminUser.role,
        permissions: oauthUser.adminUser.permissions
      };
      localStorage.setItem('current_user', JSON.stringify(adminUserData));
      window.dispatchEvent(new Event('oauthUserUpdated'));
    }
  }, [oauthInitialized, oauthUser, currentUser]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openDropdown || !event || !event.target) return;
      try {
        const clickedElement = event.target.closest('.admin-nav-dropdown');
        if (!clickedElement) {
          setOpenDropdown(null);
        }
      } catch (err) {
        console.error('Error in click outside handler:', err);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdown]);

  if (!userInitialized || !oauthInitialized) {
    return (
      <div className="admin-loading">
        <p>Loading...</p>
      </div>
    );
  }
  const storedCurrentUser = localStorage.getItem('current_user');
  const storedOAuthUser = localStorage.getItem('oauth_user');
  const storedOAuthToken = localStorage.getItem('oauth_token');
  let parsedOAuthUser = null;
  try {
    if (storedOAuthUser) {
      parsedOAuthUser = JSON.parse(storedOAuthUser);
    }
  } catch (e) {

  }

  const isOAuthLoggedIn = (oauthUser || parsedOAuthUser) && (storedOAuthToken || localStorage.getItem('oauth_token'));
  const oauthUserWithRole = oauthUser || parsedOAuthUser;
  const hasAdminRole = oauthUserWithRole && oauthUserWithRole.roleId && oauthUserWithRole.permissions;
  if (!isOAuthLoggedIn) {
    return <LoginForm />;
  }
  if (isOAuthLoggedIn && !hasAdminRole) {
    return (
      <div className="admin-page">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access the admin panel.</p>
          <Link to="/" className="admin-button primary">Go Home</Link>

        </div>
      </div>
    );
  }

  const actualUser = oauthUserWithRole;

  const userPermissions = actualUser?.permissions || [];
  const userRoleId = actualUser?.roleId;
  const isAdminRole = userRoleId === 'admin';
  const hasAdminAccess = isAdminRole ||
    userPermissions.includes('admin.access') ||
    userPermissions.includes('products.view') ||
    userPermissions.includes('categories.view') ||
    userPermissions.includes('textures.view') ||
    userPermissions.includes('images.view') ||
    userPermissions.includes('galleries.view') ||
    userPermissions.includes('orders.view') ||
    userPermissions.includes('users.view') ||
    userPermissions.includes('roles.view') ||
    userPermissions.includes('admin.settings') ||
    userPermissions.includes('jobs.view') ||
    userPermissions.includes('applications.view') ||
    userPermissions.includes('news.view') ||
    userPermissions.some((permission) => PUBLICATION_PERMISSIONS.includes(permission)) ||
    userPermissions.some((permission) => STAFF_ROSTER_PERMISSIONS.includes(permission));
  if (actualUser && !hasAdminAccess) {
    return (
      <div className="admin-page">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access the admin panel.</p>
          <Link to="/" className="admin-button primary">Go Home</Link>
        </div>
      </div>
    );
  }
  if (actualUser && !isInitialized) {
    return (
      <div className="admin-loading">
        <p>Loading admin panel...</p>
      </div>
    );
  }
  return (
    <div className="admin-page">
      {}
      <nav className="admin-navbar">
        <div className="admin-nav-container">
          <Link to="/" className="admin-nav-logo">
            VELOURA
          </Link>
          <div className="admin-nav-tabs">
            {}
            {(hasPermission('products.view') || hasPermission('categories.view') || hasPermission('products.edit')) && (
              <div
                className="admin-nav-dropdown"
                onMouseEnter={() => setOpenDropdown('content')}
                onMouseLeave={(e) => {
                  if (e.relatedTarget && e.relatedTarget instanceof Node) {
                    const menu = e.currentTarget.querySelector('.admin-nav-dropdown-menu');
                    if (!menu || !menu.contains(e.relatedTarget)) {
                      setTimeout(() => {
                        if (openDropdown === 'content') {
                          const dropdown = e.currentTarget;
                          if (dropdown && !dropdown.matches(':hover')) {
                            setOpenDropdown(null);
                          }
                        }
                      }, 200);
                    }
                  } else {
                    setOpenDropdown(null);
                  }
                }}
              >
                <button
                  className={`admin-nav-tab ${['products', 'categories', 'textures'].includes(activeTab) ? 'active' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'content' ? null : 'content')}
                >
                  Content
                  <span className="admin-nav-arrow">▼</span>
                </button>
                {openDropdown === 'content' && (
                  <div
                    className="admin-nav-dropdown-menu"
                    onMouseEnter={() => setOpenDropdown('content')}
                    onMouseLeave={(e) => {
                      if (e.relatedTarget && e.relatedTarget instanceof Node) {
                        const button = e.currentTarget.parentElement?.querySelector('.admin-nav-tab');
                        if (!button || !button.contains(e.relatedTarget)) {
                          setOpenDropdown(null);
                        }
                      } else {
                        setOpenDropdown(null);
                      }
                    }}
                  >
                    {hasPermission('products.view') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'products' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('products');
                          setOpenDropdown(null);
                        }}
                      >
                        Products
                      </button>
                    )}
                    {hasPermission('categories.view') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'categories' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('categories');
                          setOpenDropdown(null);
                        }}
                      >
                        Categories
                      </button>
                    )}
                    {hasPermission('products.edit') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'textures' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('textures');
                          setOpenDropdown(null);
                        }}
                      >
                        Textures
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {}
            {(hasPermission('images.view') || hasPermission('galleries.view')) && (
              <div
                className="admin-nav-dropdown"
                onMouseEnter={() => setOpenDropdown('media')}
                onMouseLeave={(e) => {
                  if (e.relatedTarget && e.relatedTarget instanceof Node) {
                    const menu = e.currentTarget.querySelector('.admin-nav-dropdown-menu');
                    if (!menu || !menu.contains(e.relatedTarget)) {
                      setTimeout(() => {
                        if (openDropdown === 'media') {
                          const dropdown = e.currentTarget;
                          if (dropdown && !dropdown.matches(':hover')) {
                            setOpenDropdown(null);
                          }
                        }
                      }, 200);
                    }
                  } else {
                    setOpenDropdown(null);
                  }
                }}
              >
                <button
                  className={`admin-nav-tab ${['images', 'galleries'].includes(activeTab) ? 'active' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'media' ? null : 'media')}
                >
                  Media
                  <span className="admin-nav-arrow">▼</span>
                </button>
                {openDropdown === 'media' && (
                  <div
                    className="admin-nav-dropdown-menu"
                    onMouseEnter={() => setOpenDropdown('media')}
                    onMouseLeave={(e) => {
                      if (e.relatedTarget && e.relatedTarget instanceof Node) {
                        const button = e.currentTarget.parentElement?.querySelector('.admin-nav-tab');
                        if (!button || !button.contains(e.relatedTarget)) {
                          setOpenDropdown(null);
                        }
                      } else {
                        setOpenDropdown(null);
                      }
                    }}
                  >
                    {hasPermission('images.view') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'images' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('images');
                          setOpenDropdown(null);
                        }}
                      >
                        Landing Images
                      </button>
                    )}
                    {hasPermission('galleries.view') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'galleries' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('galleries');
                          setOpenDropdown(null);
                        }}
                      >
                        Galleries
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {}
            {hasPermission('orders.view') && (
              <div
                className="admin-nav-dropdown"
                onMouseEnter={() => setOpenDropdown('store')}
                onMouseLeave={(e) => {
                  if (e.relatedTarget && e.relatedTarget instanceof Node) {
                    const menu = e.currentTarget.querySelector('.admin-nav-dropdown-menu');
                    if (!menu || !menu.contains(e.relatedTarget)) {
                      setTimeout(() => {
                        if (openDropdown === 'store') {
                          const dropdown = e.currentTarget;
                          if (dropdown && !dropdown.matches(':hover')) {
                            setOpenDropdown(null);
                          }
                        }
                      }, 200);
                    }
                  } else {
                    setOpenDropdown(null);
                  }
                }}
              >
                <button
                  className={`admin-nav-tab ${['orders', 'sales-data'].includes(activeTab) ? 'active' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'store' ? null : 'store')}
                >
                  Store
                  <span className="admin-nav-arrow">▼</span>
                </button>
                {openDropdown === 'store' && (
                  <div
                    className="admin-nav-dropdown-menu"
                    onMouseEnter={() => setOpenDropdown('store')}
                    onMouseLeave={(e) => {
                      if (e.relatedTarget && e.relatedTarget instanceof Node) {
                        const button = e.currentTarget.parentElement?.querySelector('.admin-nav-tab');
                        if (!button || !button.contains(e.relatedTarget)) {
                          setOpenDropdown(null);
                        }
                      } else {
                        setOpenDropdown(null);
                      }
                    }}
                  >
                    <button
                      className={`admin-nav-dropdown-item ${activeTab === 'orders' ? 'active' : ''}`}
                      onClick={() => {
                        changeTab('orders');
                        setOpenDropdown(null);
                      }}
                    >
                      Orders
                    </button>
                    <button
                      className={`admin-nav-dropdown-item ${activeTab === 'sales-data' ? 'active' : ''}`}
                      onClick={() => {
                        changeTab('sales-data');
                        setOpenDropdown(null);
                      }}
                    >
                      Sales Data
                    </button>
                  </div>
                )}
              </div>
            )}
            {}
            {hasAnyPermission(['jobs.view', 'jobs.create', 'jobs.edit', 'jobs.delete', 'jobs.archive', 'applications.view', 'applications.create', 'applications.edit', 'applications.delete', 'applications.archive']) && (
              <div
                className="admin-nav-dropdown"
                onMouseEnter={() => setOpenDropdown('careers')}
                onMouseLeave={(e) => {
                  if (e.relatedTarget && e.relatedTarget instanceof Node) {
                    const menu = e.currentTarget.querySelector('.admin-nav-dropdown-menu');
                    if (!menu || !menu.contains(e.relatedTarget)) {
                      setTimeout(() => {
                        if (openDropdown === 'careers') {
                          const dropdown = e.currentTarget;
                          if (dropdown && !dropdown.matches(':hover')) {
                            setOpenDropdown(null);
                          }
                        }
                      }, 200);
                    }
                  } else {
                    setOpenDropdown(null);
                  }
                }}
              >
                <button
                  className={`admin-nav-tab ${['jobs', 'applications'].includes(activeTab) ? 'active' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'careers' ? null : 'careers')}
                >
                  Careers
                  <span className="admin-nav-arrow">▼</span>
                </button>
                {openDropdown === 'careers' && (
                  <div
                    className="admin-nav-dropdown-menu"
                    onMouseEnter={() => setOpenDropdown('careers')}
                    onMouseLeave={(e) => {
                      if (e.relatedTarget && e.relatedTarget instanceof Node) {
                        const button = e.currentTarget.parentElement?.querySelector('.admin-nav-tab');
                        if (!button || !button.contains(e.relatedTarget)) {
                          setOpenDropdown(null);
                        }
                      } else {
                        setOpenDropdown(null);
                      }
                    }}
                  >
                    {hasPermission('jobs.view') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'jobs' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('jobs');
                          setOpenDropdown(null);
                        }}
                      >
                        Job Positions
                      </button>
                    )}
                    {hasPermission('applications.view') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'applications' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('applications');
                          setOpenDropdown(null);
                        }}
                      >
                        Applications
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {}
            {canManageStaffRoster && (
              <div
                className="admin-nav-dropdown"
                onMouseEnter={() => setOpenDropdown('ooc')}
                onMouseLeave={(e) => {
                  if (e.relatedTarget && e.relatedTarget instanceof Node) {
                    const menu = e.currentTarget.querySelector('.admin-nav-dropdown-menu');
                    if (!menu || !menu.contains(e.relatedTarget)) {
                      setTimeout(() => {
                        if (openDropdown === 'ooc') {
                          const dropdown = e.currentTarget;
                          if (dropdown && !dropdown.matches(':hover')) {
                            setOpenDropdown(null);
                          }
                        }
                      }, 200);
                    }
                  } else {
                    setOpenDropdown(null);
                  }
                }}
              >
                <button
                  className={`admin-nav-tab ${activeTab === 'staff-roster' ? 'active' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'ooc' ? null : 'ooc')}
                >
                  OOC
                  <span className="admin-nav-arrow">{'\u25BC'}</span>
                </button>
                {openDropdown === 'ooc' && (
                  <div
                    className="admin-nav-dropdown-menu"
                    onMouseEnter={() => setOpenDropdown('ooc')}
                    onMouseLeave={(e) => {
                      if (e.relatedTarget && e.relatedTarget instanceof Node) {
                        const button = e.currentTarget.parentElement?.querySelector('.admin-nav-tab');
                        if (!button || !button.contains(e.relatedTarget)) {
                          setOpenDropdown(null);
                        }
                      } else {
                        setOpenDropdown(null);
                      }
                    }}
                  >
                    <button
                      className={`admin-nav-dropdown-item ${activeTab === 'staff-roster' ? 'active' : ''}`}
                      onClick={() => {
                        changeTab('staff-roster');
                        setOpenDropdown(null);
                      }}
                    >
                      Staff Roster
                    </button>
                  </div>
                )}
              </div>
            )}
            {}
            {(hasPermission('users.view') || hasPermission('roles.view') || hasPermission('admin.settings') || hasAnyPermission(['news.view', 'news.create', 'news.edit', 'news.delete']) || canManagePublications || isAdministrator) && (
              <div
                className="admin-nav-dropdown"
                onMouseEnter={() => setOpenDropdown('system')}
                onMouseLeave={(e) => {
                  if (e.relatedTarget && e.relatedTarget instanceof Node) {
                    const menu = e.currentTarget.querySelector('.admin-nav-dropdown-menu');
                    if (!menu || !menu.contains(e.relatedTarget)) {
                      setTimeout(() => {
                        if (openDropdown === 'system') {
                          const dropdown = e.currentTarget;
                          if (dropdown && !dropdown.matches(':hover')) {
                            setOpenDropdown(null);
                          }
                        }
                      }, 200);
                    }
                  } else {
                    setOpenDropdown(null);
                  }
                }}
              >
                <button
                className={`admin-nav-tab ${['users', 'roles', 'partners', 'news', 'team', 'publications', 'pages', 'maintenance'].includes(activeTab) ? 'active' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'system' ? null : 'system')}
                >
                  System
                  <span className="admin-nav-arrow">▼</span>
                </button>
                {openDropdown === 'system' && (
                  <div
                    className="admin-nav-dropdown-menu"
                    onMouseEnter={() => setOpenDropdown('system')}
                    onMouseLeave={(e) => {
                      if (e.relatedTarget && e.relatedTarget instanceof Node) {
                        const button = e.currentTarget.parentElement?.querySelector('.admin-nav-tab');
                        if (!button || !button.contains(e.relatedTarget)) {
                          setOpenDropdown(null);
                        }
                      } else {
                        setOpenDropdown(null);
                      }
                    }}
                  >
                    {hasPermission('users.view') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('users');
                          setOpenDropdown(null);
                        }}
                      >
                        Users
                      </button>
                    )}
                    {hasPermission('roles.view') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'roles' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('roles');
                          setOpenDropdown(null);
                        }}
                      >
                        Roles
                      </button>
                    )}
                    {hasPermission('admin.access') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'discount-codes' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('discount-codes');
                          setOpenDropdown(null);
                        }}
                      >
                        Discount Codes
                      </button>
                    )}
                    {hasPermission('admin.settings') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'partners' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('partners');
                          setOpenDropdown(null);
                        }}
                      >
                        Partners
                      </button>
                    )}
                    {hasAnyPermission(['news.view', 'news.create', 'news.edit', 'news.delete']) && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'news' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('news');
                          setOpenDropdown(null);
                        }}
                      >
                        News
                      </button>
                    )}
                    {hasPermission('admin.settings') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'team' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('team');
                          setOpenDropdown(null);
                        }}
                      >
                        Team
                      </button>
                    )}
                    {canManagePublications && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'publications' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('publications');
                          setOpenDropdown(null);
                        }}
                      >
                        Publications
                      </button>
                    )}
                    {hasPermission('admin.settings') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'pages' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('pages');
                          setOpenDropdown(null);
                        }}
                      >
                        Pages
                      </button>
                    )}
                    {hasPermission('admin.settings') && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'maintenance' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('maintenance');
                          setOpenDropdown(null);
                        }}
                      >
                        Maintenance
                      </button>
                    )}
                    {isAdministrator && (
                      <button
                        className={`admin-nav-dropdown-item ${activeTab === 'webhooks' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('webhooks');
                          setOpenDropdown(null);
                        }}
                      >
                        Webhooks
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {currentUser && (
              <button
                className="admin-nav-tab admin-nav-logout"
                onClick={handleLogout}
              >
                Logout ({currentUser.username})
              </button>
            )}
          </div>
          {}
          <button
            className={`admin-burger-menu ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {}
        <div
          className={`admin-mobile-tabs-overlay ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div className="admin-mobile-tabs" onClick={(e) => e.stopPropagation()}>
            {}
            {(hasPermission('products.view') || hasPermission('categories.view') || hasPermission('products.edit')) && (
              <div className="admin-mobile-section">
                <button
                  className="admin-mobile-section-header"
                  onClick={() => setOpenMobileSection(openMobileSection === 'content' ? null : 'content')}
                >
                  Content
                  <span className={`admin-mobile-arrow ${openMobileSection === 'content' ? 'open' : ''}`}>▼</span>
                </button>
                {openMobileSection === 'content' && (
                  <div className="admin-mobile-section-items">
                    {hasPermission('products.view') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'products' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab('products');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Products
                      </button>
                    )}
                    {hasPermission('categories.view') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'categories' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('categories');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Categories
                      </button>
                    )}
                    {hasPermission('products.edit') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'textures' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('textures');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Textures
                      </button>
                    )}
                  </div>

                )}
              </div>
            )}
            {}
            {(hasPermission('images.view') || hasPermission('galleries.view')) && (
              <div className="admin-mobile-section">
                <button
                  className="admin-mobile-section-header"
                  onClick={() => setOpenMobileSection(openMobileSection === 'media' ? null : 'media')}
                >
                  Media
                  <span className={`admin-mobile-arrow ${openMobileSection === 'media' ? 'open' : ''}`}>▼</span>
                </button>
                {openMobileSection === 'media' && (
                  <div className="admin-mobile-section-items">
                    {hasPermission('images.view') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'images' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('images');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Landing Images
                      </button>
                    )}
                    {hasPermission('galleries.view') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'galleries' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('galleries');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Galleries
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {}
            {hasPermission('orders.view') && (
              <div className="admin-mobile-section">
                <button
                  className="admin-mobile-section-header"
                  onClick={() => setOpenMobileSection(openMobileSection === 'store' ? null : 'store')}
                >
                  Store
                  <span className={`admin-mobile-arrow ${openMobileSection === 'store' ? 'open' : ''}`}>▼</span>
                </button>
                {openMobileSection === 'store' && (
                  <div className="admin-mobile-section-items">
                    <button
                      className={`admin-nav-tab ${activeTab === 'orders' ? 'active' : ''}`}
                      onClick={() => {
                        changeTab('orders');
                        setIsMobileMenuOpen(false);
                        setOpenMobileSection(null);
                      }}
                    >
                      Orders
                    </button>
                    <button
                      className={`admin-nav-tab ${activeTab === 'sales-data' ? 'active' : ''}`}
                      onClick={() => {
                        changeTab('sales-data');
                        setIsMobileMenuOpen(false);
                        setOpenMobileSection(null);
                      }}
                    >
                      Sales Data
                    </button>
                  </div>
                )}
              </div>
            )}
            {}
            {hasAnyPermission(['jobs.view', 'jobs.create', 'jobs.edit', 'jobs.delete', 'jobs.archive', 'applications.view', 'applications.create', 'applications.edit', 'applications.delete', 'applications.archive']) && (
              <div className="admin-mobile-section">
                <button
                  className="admin-mobile-section-header"
                  onClick={() => setOpenMobileSection(openMobileSection === 'careers' ? null : 'careers')}
                >
                  Careers
                  <span className={`admin-mobile-arrow ${openMobileSection === 'careers' ? 'open' : ''}`}>▼</span>
                </button>
                {openMobileSection === 'careers' && (
                  <div className="admin-mobile-section-items">
                    {hasPermission('jobs.view') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'jobs' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('jobs');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Job Positions
                      </button>
                    )}
                    {hasPermission('applications.view') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'applications' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('applications');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Applications
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {}
            {canManageStaffRoster && (
              <div className="admin-mobile-section">
                <button
                  className="admin-mobile-section-header"
                  onClick={() => setOpenMobileSection(openMobileSection === 'ooc' ? null : 'ooc')}
                >
                  Out of Character
                  <span className={`admin-mobile-arrow ${openMobileSection === 'ooc' ? 'open' : ''}`}>{'\u25BC'}</span>
                </button>
                {openMobileSection === 'ooc' && (
                  <div className="admin-mobile-section-items">
                    <button
                      className={`admin-nav-tab ${activeTab === 'staff-roster' ? 'active' : ''}`}
                      onClick={() => {
                        changeTab('staff-roster');
                        setIsMobileMenuOpen(false);
                        setOpenMobileSection(null);
                      }}
                    >
                      Staff Roster
                    </button>
                  </div>
                )}
              </div>
            )}
            {}
            {(hasPermission('users.view') || hasPermission('roles.view') || hasPermission('admin.settings') || hasAnyPermission(['news.view', 'news.create', 'news.edit', 'news.delete']) || canManagePublications || isAdministrator) && (
              <div className="admin-mobile-section">
                <button
                  className="admin-mobile-section-header"
                  onClick={() => setOpenMobileSection(openMobileSection === 'system' ? null : 'system')}
                >
                  System
                  <span className={`admin-mobile-arrow ${openMobileSection === 'system' ? 'open' : ''}`}>▼</span>
                </button>
                {openMobileSection === 'system' && (
                  <div className="admin-mobile-section-items">
                    {hasPermission('users.view') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('users');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Users
                      </button>
                    )}
                    {hasPermission('roles.view') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'roles' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('roles');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Roles
                      </button>
                    )}
                    {hasPermission('admin.access') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'discount-codes' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('discount-codes');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Discount Codes
                      </button>

                    )}
                    {hasPermission('admin.settings') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'partners' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('partners');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Partners
                      </button>

                    )}
                    {hasAnyPermission(['news.view', 'news.create', 'news.edit', 'news.delete']) && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'news' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('news');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        News
                      </button>

                    )}
                    {hasPermission('admin.settings') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'team' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('team');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Team
                      </button>

                    )}
                    {canManagePublications && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'publications' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('publications');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Publications
                      </button>
                    )}
                    {hasPermission('admin.settings') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'pages' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('pages');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Pages
                      </button>

                    )}
                    {hasPermission('admin.settings') && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('maintenance');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Maintenance
                      </button>
                    )}
                    {isAdministrator && (
                      <button
                        className={`admin-nav-tab ${activeTab === 'webhooks' ? 'active' : ''}`}
                        onClick={() => {
                          changeTab('webhooks');
                          setIsMobileMenuOpen(false);
                          setOpenMobileSection(null);
                        }}
                      >
                        Webhooks
                      </button>

                    )}
                  </div>

                )}
              </div>

            )}

            {currentUser && (
              <button
                className="admin-nav-tab admin-nav-logout"
                onClick={handleLogout}
              >
                Logout ({currentUser.username})
              </button>

            )}
          </div>

        </div>

      </nav>


      {}
      <div className="admin-content">
        <div className="admin-container">
          {activeTab === 'products' && hasPermission('products.view') && <ProductsManagement />}
          {activeTab === 'categories' && hasPermission('categories.view') && <CategoriesManagement />}
          {activeTab === 'textures' && hasPermission('products.edit') && <TexturesManagement />}
          {activeTab === 'images' && hasPermission('images.view') && <ImagesManagement />}
          {activeTab === 'galleries' && hasPermission('galleries.view') && <GalleriesManagement />}
          {activeTab === 'orders' && hasPermission('orders.view') && <OrdersManagement />}
          {activeTab === 'sales-data' && hasPermission('orders.view') && <SalesDataManagement />}
          {activeTab === 'jobs' && hasPermission('jobs.view') && <JobsManagement />}
          {activeTab === 'applications' && hasPermission('applications.view') && <ApplicationsManagement />}
          {activeTab === 'users' && hasPermission('users.view') && <UsersManagement />}
          {activeTab === 'roles' && hasPermission('roles.view') && <RolesManagement />}
          {activeTab === 'webhooks' && isAdministrator && <WebhooksManagement />}
          {activeTab === 'discount-codes' && hasPermission('admin.access') && <DiscountCodesManagement />}
          {activeTab === 'partners' && hasPermission('admin.settings') && <PartnersManagement />}
          {activeTab === 'news' && hasPermission('news.view') && <NewsManagement />}
          {activeTab === 'team' && hasPermission('admin.settings') && <TeamManagement />}
          {activeTab === 'publications' && canManagePublications && <PublicationsManagement />}
          {activeTab === 'staff-roster' && canManageStaffRoster && <StaffRosterManagement />}
          {activeTab === 'pages' && hasPermission('admin.settings') && <SiteSettingsManagement />}
          {activeTab === 'maintenance' && hasPermission('admin.settings') && <MaintenanceManagement />}
        </div>

      </div>

    </div>

  );
};

export default Admin;
